import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import type { DeploymentSummary } from '@/api/deploy';
import { useEnvironments } from '@/hooks/use-configuration';
import { useDeploymentDetail, useDeploymentHistory } from '@/hooks/use-deployment-history';

// --- Types ---

type StatusFilterValue = 'all' | 'RUNNING' | 'SUCCESS' | 'FAILED';

interface HistoryTabProps {
  projectKey: string;
}

// --- Filter Options ---

const FILTER_OPTIONS: { label: string; value: StatusFilterValue }[] = [
  { label: 'All', value: 'all' },
  { label: 'Running', value: 'RUNNING' },
  { label: 'Successful', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
];

// --- Type & Asset Helpers ---

/**
 * Maps a deployment record to its high-level "Type" label.
 * APP maps to "App or Service"; EC2-related types map to "EC2 Instance".
 */
function getTypeLabel(record: DeploymentSummary): string {
  const typeLabels: Record<string, string> = {
    APP: 'App or Service',
    APP_S3: 'App or Service',
    CONFIG: 'Configuration',
    SERVICE: 'Service',
    DB_SCRIPT: 'DB Script',
    SERVER_UP: 'EC2 Instance',
    REBOOT: 'EC2 Instance',
    AWS_EC2: 'EC2 Instance',
    PLAYBOOK: 'Playbook',
  };
  return typeLabels[record.type] ?? record.type ?? '-';
}

/**
 * Derives the human-readable "Asset" label for a deployment record.
 * Prefers the backend-resolved assetLabel (e.g. "Restart - Payments API",
 * "database.sql", "app-web"), falling back through artifactId -> type label.
 */
function getAssetLabel(record: DeploymentSummary): string {
  if (record.assetLabel) return record.assetLabel;
  if (record.artifactId) return record.artifactId;
  return getTypeLabel(record);
}

// --- Status Badge ---

function StatusBadge({ status }: { status: DeploymentSummary['status'] }) {
  const config: Record<DeploymentSummary['status'], { label: string; dot: string; text: string; bg: string }> = {
    SUCCESS: { label: 'Success', dot: 'bg-green-500', text: 'text-green-700 dark:text-green-300', bg: 'bg-green-50 dark:bg-green-900/20' },
    FAILED: { label: 'Failed', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-900/20' },
    RUNNING: { label: 'Running', dot: 'bg-yellow-500 animate-pulse', text: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
    QUEUED: { label: 'Queued', dot: 'bg-gray-400', text: 'text-content-secondary', bg: 'bg-surface-raised' },
  };

  const { label, dot, text, bg } = config[status];

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${text} ${bg}`}>
      <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

// --- Deployment Log Panel (inline under row) ---

function DeploymentLogPanel({ runId }: { runId: string }) {
  const { data: detail, isLoading, isError } = useDeploymentDetail(runId);

  return (
    <tr>
      <td colSpan={8} className="px-3 py-3 border-b border-border bg-surface">
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h4 className="text-sm font-semibold text-content">Deployment Log</h4>

          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="font-medium text-content-secondary">Run ID:</dt>
            <dd className="font-mono text-content">{runId}</dd>
            {!isLoading && !isError && detail?.command && (
              <>
                <dt className="font-medium text-content-secondary">Command:</dt>
                <dd className="font-mono text-content break-all">{detail.command}</dd>
              </>
            )}
          </dl>

          {isLoading && (
            <div className="animate-pulse space-y-2">
              <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          )}

          {isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load deployment log
            </p>
          )}

          {!isLoading && !isError && detail?.log == null && (
            <p className="text-sm text-content-tertiary">No log available</p>
          )}

          {!isLoading && !isError && detail?.log != null && (
            <div>
              <p className="text-xs font-medium text-content-secondary mb-1">Output:</p>
              <div className="max-h-[600px] overflow-y-auto rounded-md bg-surface-raised p-3">
                <pre className="whitespace-pre-wrap break-words text-xs font-mono text-content">
                  {detail.log}
                </pre>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// --- Constants ---

/** Deployment types excluded from history when system checks are hidden (server-side filter) */
const SYSTEM_CHECK_EXCLUDE_TYPES = 'SERVER_UP';

/** RunId prefixes that indicate system checks (server-side filter) */
const SYSTEM_CHECK_EXCLUDE_PREFIXES = 'server-check,service-check';

// --- Component ---

export function HistoryTab({ projectKey }: HistoryTabProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [showSystemChecks, setShowSystemChecks] = useState(false);

  const { data, isLoading, isError, refetch } = useDeploymentHistory({
    projectKey,
    page: currentPage,
    status: statusFilter === 'all' ? undefined : statusFilter,
    excludeTypes: showSystemChecks ? undefined : SYSTEM_CHECK_EXCLUDE_TYPES,
    excludeRunIdPrefixes: showSystemChecks ? undefined : SYSTEM_CHECK_EXCLUDE_PREFIXES,
  });

  const { data: environments = [] } = useEnvironments(projectKey);

  // Resolve environment ID to display name
  const resolveEnvName = (envId?: string) => {
    if (!envId) return '-';
    return environments.find((e) => e.id === envId)?.name ?? envId;
  };

  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: ['deployments', 'detail'] }),
    ]);
    setIsRefreshing(false);
  };

  return (
    <div className="space-y-4">
      {/* StatusFilterBar - always visible regardless of data state */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setStatusFilter(option.value);
                setCurrentPage(0);
                setExpandedRunId(null);
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
                statusFilter === option.value
                  ? 'bg-brand-700 text-white'
                  : 'border border-border text-content-secondary hover:bg-surface-raised'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {/* System checks toggle */}
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showSystemChecks}
              onChange={(e) => setShowSystemChecks(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border text-brand-600 focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            />
            <span className="text-xs text-content-secondary">Include system checks</span>
          </label>

          {/* Refresh button */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Refresh deployment history"
          >
            <svg
              className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {/* Error state with retry */}
      {isError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-center">
          <p className="text-sm text-red-700 dark:text-red-300">
            Failed to load deployment history.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && (!data || data.content.length === 0) && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <p className="text-sm text-content-tertiary">
            No deployments found for the selected filter.
          </p>
        </div>
      )}

      {/* Table and pagination - only when data exists */}
      {!isLoading && !isError && data && data.content.length > 0 && (
        <>
          {/* DeploymentHistoryTable */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-raised text-left">
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Asset</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Type</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Status</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Environment</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">User</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Version</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Started</th>
                  <th className="px-3 py-2 text-xs font-medium text-content-secondary">Completed</th>
                </tr>
              </thead>
              <tbody>
                {data.content.map((record) => (
                  <Fragment key={record.runId}>
                    <tr
                      onClick={() =>
                        setExpandedRunId(expandedRunId === record.runId ? null : record.runId)
                      }
                      className={`cursor-pointer border-b border-border transition-colors hover:bg-surface-raised ${
                        expandedRunId === record.runId ? 'bg-surface-raised' : ''
                      }`}
                    >
                      <td className="px-3 py-2 text-xs font-medium text-content">{getAssetLabel(record)}</td>
                      <td className="px-3 py-2 text-xs text-content-secondary">{getTypeLabel(record)}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="px-3 py-2 text-content">{resolveEnvName(record.environment)}</td>
                      <td className="px-3 py-2 text-content">
                        <span className="inline-flex items-center gap-1.5">
                          {record.displayName ?? '-'}
                          {record.user === 'system:ci-auto-deploy' && (
                            <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 text-[10px] font-semibold text-purple-700 dark:text-purple-300">
                              Auto-Deploy
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-content">{record.version || '-'}</td>
                      <td className="px-3 py-2 text-content-secondary">{new Date(record.started).toLocaleString()}</td>
                      <td className="px-3 py-2 text-content-secondary">{record.completed ? new Date(record.completed).toLocaleString() : '-'}</td>
                    </tr>
                    {expandedRunId === record.runId && (
                      <DeploymentLogPanel runId={record.runId} />
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* PaginationControls */}
          {data.totalElements > 25 && (
            <div className="flex items-center justify-between rounded-md border border-border px-4 py-2">
              <span className="text-xs text-content-secondary">
                Total: {data.totalElements} deployments
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-xs text-content-secondary">
                  Page {currentPage + 1} of {data.totalPages}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= data.totalPages - 1}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
